// Shell: navegação e montagem de feature. A feature ativa é estado, e o NAV é
// um array filtrado por permissão na hora de renderizar — quem decide o que
// montar continua sendo este arquivo, não a URL. O que `lib/rota` faz é manter
// a barra de endereço contando a mesma história, para que Voltar, recarregar e
// mandar o link de uma tela funcionem.
//
// O AbortController por feature não é detalhe: sem ele, os listeners da
// feature anterior continuam vivos e disparam em cliques que não são deles.
// Toda feature recebe o signal e registra seus eventos com { signal }.

import { pode, getSessao, limparSessao } from '../lib/permissions';
import { getSupabase } from '../lib/supabase';
import { env } from '../lib/env';
import { esc } from '../lib/format';
import { pintar } from '../lib/pintar';
import { aoNavegar, escreverRota, lerRota } from '../lib/rota';

export interface ContextoFeature {
  /** Onde a feature renderiza. Já vem vazio. */
  raiz: HTMLElement;
  /** Abortado quando o usuário navega para outra feature. */
  signal: AbortSignal;
}

export interface Feature {
  montar(ctx: ContextoFeature): void | Promise<void>;
}

interface ItemSimples {
  tipo: 'item';
  nome: string;
  label: string;
  /** Permissão mínima para o item aparecer. */
  permissao: string;
}

interface Grupo {
  tipo: 'grupo';
  label: string;
  itens: ItemSimples[];
}

type ItemNav = ItemSimples | Grupo;

// Ordem do menu = ordem de uso no dia. O mapa vem primeiro porque é o que a
// recepção abre de manhã e mantém aberto.
const NAV: ItemNav[] = [
  { tipo: 'item', nome: 'painel', label: 'Painel', permissao: 'mapa.ver' },
  { tipo: 'item', nome: 'mapa-dia', label: 'Mapa do dia', permissao: 'mapa.ver' },
  { tipo: 'item', nome: 'pacientes', label: 'Pacientes', permissao: 'pacientes.ver' },
  {
    tipo: 'grupo',
    label: 'Caixa',
    itens: [
      { tipo: 'item', nome: 'sobras', label: 'Sobras de caixa', permissao: 'sobras.ver' },
      {
        tipo: 'item',
        nome: 'mapa-conferencia',
        label: 'Conferência',
        permissao: 'conferencia.ver',
      },
      { tipo: 'item', nome: 'conciliacao', label: 'Conciliação bancária', permissao: 'sobras.ver' },
      { tipo: 'item', nome: 'comissao', label: 'Comissão CRC', permissao: 'repasse.ver' },
    ],
  },
  { tipo: 'item', nome: 'funil', label: 'Funil e follow-up', permissao: 'crc.ver' },
  { tipo: 'item', nome: 'agenda', label: 'Agenda e orçamentos', permissao: 'agenda.ver' },
  {
    tipo: 'grupo',
    label: 'Equipe',
    itens: [
      { tipo: 'item', nome: 'escala', label: 'Escala de cadeira', permissao: 'escala.ver' },
      { tipo: 'item', nome: 'repasse', label: 'Repasse ao dentista', permissao: 'repasse.ver' },
    ],
  },
  {
    tipo: 'grupo',
    label: 'Sistema',
    itens: [
      { tipo: 'item', nome: 'cadastros', label: 'Cadastros', permissao: 'configuracoes.ver' },
      { tipo: 'item', nome: 'equipe', label: 'Acesso da equipe', permissao: 'equipe.ver' },
      { tipo: 'item', nome: 'sync-status', label: 'Sincronização', permissao: 'sync.ver' },
      { tipo: 'item', nome: 'configuracoes', label: 'Parâmetros', permissao: 'configuracoes.ver' },
    ],
  },
];

// Registro de features. Import dinâmico: cada uma vira seu próprio chunk.
const FEATURES: Record<string, () => Promise<{ default: Feature }>> = {
  painel: () => import('../features/painel/index'),
  'mapa-dia': () => import('../features/mapa-dia/index'),
  configuracoes: () => import('../features/configuracoes/index'),
  cadastros: () => import('../features/cadastros/index'),
  equipe: () => import('../features/equipe/index'),
  pacientes: () => import('../features/pacientes/index'),
  sobras: () => import('../features/sobras/index'),
  'sync-status': () => import('../features/sync-status/index'),
  'mapa-conferencia': () => import('../features/mapa-conferencia/index'),
  conciliacao: () => import('../features/conciliacao/index'),
  comissao: () => import('../features/comissao/index'),
  escala: () => import('../features/escala/index'),
  funil: () => import('../features/funil/index'),
  repasse: () => import('../features/repasse/index'),
  agenda: () => import('../features/agenda/index'),
};

const CHAVE_ULTIMA_FEATURE = 'cleandental:ultimaFeature';

let abortAtual: AbortController | null = null;
let featureAtual: string | null = null;

/**
 * Qual grupo do menu está aberto, pelo rótulo.
 *
 * Antes não havia estado nenhum: o submenu abria por `:hover` e por
 * `:focus-within`, e as duas coisas juntas davam a impressão de funcionar.
 * Não funcionavam. Os itens ficam em `display: none`, e elemento nessa
 * situação não recebe foco — logo `:focus-within` nunca disparava pelo
 * teclado, e o Tab pulava os submenus inteiros. Oito das quinze telas não
 * tinham caminho de teclado nenhum. No toque era pior: o rótulo era um
 * `<span>`, que não é clicável nem focável, então tablet dependia de o
 * navegador simular hover.
 */
let grupoAberto: string | null = null;

/**
 * Os listeners do shell, para poder desligá-los.
 *
 * `montarShell` pode rodar mais de uma vez na mesma página — o supabase-js
 * emite SIGNED_IN de novo ao recuperar a sessão, e `iniciar()` remonta. Os
 * listeners ficam no `#app`, que não é recriado, então sem isto eles se
 * acumulam: o segundo handler de clique abriria e fecharia o mesmo submenu no
 * mesmo gesto, e o menu passaria a "não abrir" sem nenhum erro no console.
 */
let shellAbort: AbortController | null = null;

function itensVisiveis(): ItemNav[] {
  const visiveis: ItemNav[] = [];
  for (const item of NAV) {
    if (item.tipo === 'item') {
      if (pode(item.permissao)) visiveis.push(item);
    } else {
      const filhos = item.itens.filter((i) => pode(i.permissao));
      if (filhos.length > 0) visiveis.push({ ...item, itens: filhos });
    }
  }
  return visiveis;
}

function primeiraFeaturePermitida(): string | null {
  for (const item of itensVisiveis()) {
    if (item.tipo === 'item') return item.nome;
    const primeiro = item.itens[0];
    if (primeiro) return primeiro.nome;
  }
  return null;
}

function podeAcessar(nome: string): boolean {
  for (const item of NAV) {
    if (item.tipo === 'item' && item.nome === nome) return pode(item.permissao);
    if (item.tipo === 'grupo') {
      const achado = item.itens.find((i) => i.nome === nome);
      if (achado) return pode(achado.permissao);
    }
  }
  return false;
}

function renderNav(ativa: string | null): string {
  return itensVisiveis()
    .map((item) => {
      if (item.tipo === 'item') {
        const cls = item.nome === ativa ? 'nav-item ativo' : 'nav-item';
        return `<button class="${cls}" data-feature="${esc(item.nome)}">${esc(item.label)}</button>`;
      }
      const filhos = item.itens
        .map((i) => {
          const cls = i.nome === ativa ? 'nav-sub ativo' : 'nav-sub';
          return `<button class="${cls}" data-feature="${esc(i.nome)}">${esc(i.label)}</button>`;
        })
        .join('');
      const temAtiva = item.itens.some((i) => i.nome === ativa);
      const aberto = grupoAberto === item.label;
      // O id sai do rótulo, não da posição: a lista é filtrada por permissão,
      // então a posição de um grupo muda conforme quem está olhando.
      // split/join e não só toLowerCase: id com espaço é inválido, e o dia em
      // que um grupo se chamar "Contas a pagar" o aria-controls quebraria calado.
      const idItens = `nav-itens-${item.label.toLowerCase().split(' ').join('-')}`;
      return `
        <div class="nav-grupo${temAtiva ? ' ativo' : ''}">
          <button class="nav-grupo-label" data-grupo="${esc(item.label)}"
                  aria-expanded="${aberto ? 'true' : 'false'}" aria-controls="${esc(idItens)}">
            ${esc(item.label)}<span class="nav-seta" aria-hidden="true">▾</span>
          </button>
          <div class="nav-grupo-itens" id="${esc(idItens)}"${aberto ? '' : ' hidden'}>${filhos}</div>
        </div>`;
    })
    .join('');
}

export interface OpcoesMontar {
  /** Veio do botão Voltar: a URL já está certa, escrever de novo empilharia. */
  daUrl?: boolean;
  /** Troca a entrada do histórico em vez de empilhar uma nova. */
  substituirUrl?: boolean;
}

export async function montarFeature(nome: string, opcoes: OpcoesMontar = {}): Promise<void> {
  if (!podeAcessar(nome)) {
    const alternativa = primeiraFeaturePermitida();
    if (!alternativa || alternativa === nome) {
      mostrarSemAcesso();
      return;
    }
    // Substitui: quem digitou o endereço de uma tela que não pode abrir não
    // deve ganhar uma entrada de histórico para ela. Empilhada, o Voltar
    // quicaria entre a tela negada e a alternativa sem sair do lugar.
    await montarFeature(alternativa, { substituirUrl: true });
    return;
  }

  if (!opcoes.daUrl) escreverRota(nome, {}, opcoes.substituirUrl === true);

  // Navegar fecha o submenu de onde se clicou. Deixá-lo aberto por cima da
  // tela nova é o tipo de coisa que ninguém reporta e todo mundo contorna.
  grupoAberto = null;
  abortAtual?.abort();
  abortAtual = new AbortController();
  featureAtual = nome;
  try {
    sessionStorage.setItem(CHAVE_ULTIMA_FEATURE, nome);
  } catch {
    // Navegador com armazenamento bloqueado. Só perde a memória da última tela.
  }

  const raiz = document.getElementById('conteudo');
  if (!raiz) return;
  raiz.innerHTML = '<div class="carregando">Carregando…</div>';
  atualizarNav();

  const carregar = FEATURES[nome];
  if (!carregar) {
    raiz.innerHTML = `<div class="vazio"><h2>Em construção</h2><p>A tela <strong>${esc(nome)}</strong> ainda não foi implementada.</p></div>`;
    return;
  }

  try {
    const mod = await carregar();
    if (abortAtual.signal.aborted) return;
    raiz.innerHTML = '';
    await mod.default.montar({ raiz, signal: abortAtual.signal });
  } catch (err) {
    if (abortAtual.signal.aborted) return;
    console.error(`[shell] falha ao montar "${nome}"`, err);
    raiz.innerHTML = `<div class="erro"><h2>Não foi possível abrir esta tela</h2><p>Recarregue a página. Se continuar, avise o suporte.</p></div>`;
  }
}

function atualizarNav(): void {
  const nav = document.getElementById('nav');
  // pintar e não innerHTML: abrir o grupo pelo teclado redesenha o NAV, e sem
  // isso o foco cairia no body no exato gesto que existe para dar acesso pelo
  // teclado.
  if (nav) pintar(nav, renderNav(featureAtual));
}

/** Abre ou fecha um grupo do menu, devolvendo o foco ao botão dele. */
function alternarGrupo(label: string | null): void {
  grupoAberto = grupoAberto === label ? null : label;
  atualizarNav();
  if (label === null) return;
  const seletor = `[data-grupo="${CSS.escape(label)}"]`;
  document.getElementById('nav')?.querySelector<HTMLElement>(seletor)?.focus();
}

function mostrarSemAcesso(): void {
  const raiz = document.getElementById('conteudo');
  if (raiz) {
    raiz.innerHTML = `
      <div class="vazio">
        <h2>Sem telas disponíveis</h2>
        <p>Seu usuário está aprovado, mas o papel atribuído não dá acesso a nenhuma tela. Fale com a gestão.</p>
      </div>`;
  }
}

export function montarShell(): void {
  const sessao = getSessao();
  const app = document.getElementById('app');
  if (!app || !sessao) return;

  app.innerHTML = `
    <div class="shell">
      <header class="topo">
        <div class="marca">
          <strong>Clean Dental</strong>
          <span>CD Painel</span>
        </div>
        <nav id="nav" class="nav"></nav>
        <div class="usuario">
          <span class="usuario-nome">${esc(sessao.nome ?? sessao.email ?? '')}</span>
          <span class="usuario-papel">${esc(sessao.papel ?? '')}</span>
          <button id="sair" class="botao-texto">Sair</button>
        </div>
      </header>
      <main id="conteudo" class="conteudo"></main>
      <footer class="rodape">build ${esc(env.buildId)}</footer>
    </div>`;

  shellAbort?.abort();
  shellAbort = new AbortController();
  const sinalShell = shellAbort.signal;

  app.addEventListener(
    'click',
    (ev) => {
      const origem = ev.target as HTMLElement;

      const botaoGrupo = origem.closest<HTMLElement>('[data-grupo]');
      if (botaoGrupo) {
        alternarGrupo(botaoGrupo.dataset['grupo'] ?? null);
        return;
      }

      // O nome sai do dataset ANTES de qualquer redesenho: atualizarNav troca o
      // NAV inteiro, e o botão clicado deixa de existir no meio do caminho.
      const nome = origem.closest<HTMLElement>('[data-feature]')?.dataset['feature'];
      const precisaFechar = grupoAberto !== null;
      if (precisaFechar) grupoAberto = null;

      if (nome) {
        void montarFeature(nome);
        return;
      }
      // Clique em qualquer outro lugar fecha o submenu aberto.
      if (precisaFechar) atualizarNav();
      if (origem.id === 'sair') void sair();
    },
    { signal: sinalShell },
  );

  // Esc fecha o submenu. Sem isso, quem abriu pelo teclado só sai de lá
  // tabulando por todos os itens até o fim.
  app.addEventListener(
    'keydown',
    (ev) => {
      if (ev.key !== 'Escape' || grupoAberto === null) return;
      const label = grupoAberto;
      grupoAberto = null;
      atualizarNav();
      const seletor = `[data-grupo="${CSS.escape(label)}"]`;
      document.getElementById('nav')?.querySelector<HTMLElement>(seletor)?.focus();
    },
    { signal: sinalShell },
  );

  // O botão Voltar do navegador. A URL é a fonte; o shell só monta o que ela
  // pede, sem escrever de volta.
  aoNavegar((rota) => {
    if (rota.feature) void montarFeature(rota.feature, { daUrl: true });
  });

  // A URL ganha da última tela guardada: quem abriu um link para o mapa quer o
  // mapa, não o lugar onde parou ontem. A sessionStorage continua valendo para
  // a raiz — que é onde o retorno do login por OAuth sempre cai, já que o
  // redirect volta para a origem.
  let guardada: string | null = null;
  try {
    guardada = sessionStorage.getItem(CHAVE_ULTIMA_FEATURE);
  } catch {
    guardada = null;
  }
  const daUrl = lerRota().feature;
  const inicial = daUrl ?? guardada;
  const alvo = inicial && podeAcessar(inicial) ? inicial : primeiraFeaturePermitida();
  if (!alvo) {
    mostrarSemAcesso();
    return;
  }
  if (alvo === daUrl) {
    // A URL já é a certa, INCLUSIVE os parâmetros. Reescrevê-la aqui apagaria
    // a busca antes de a feature chegar a lê-la — que é exatamente o que
    // acontecia com um link de /mapa-dia?data=2026-09-05: a data sumia entre
    // o clique e a montagem, e o link abria o dia de hoje.
    void montarFeature(alvo, { daUrl: true });
  } else {
    // Substitui em vez de empilhar: a primeira tela não é uma navegação, e
    // empilhada faria o primeiro Voltar da sessão não sair do lugar.
    void montarFeature(alvo, { substituirUrl: true });
  }
}

async function sair(): Promise<void> {
  abortAtual?.abort();
  limparSessao();
  try {
    sessionStorage.removeItem(CHAVE_ULTIMA_FEATURE);
  } catch {
    // ignora
  }
  // Volta a URL para a raiz: sem isto, quem entrar depois cai na última tela
  // de quem saiu — e, se não tiver permissão para ela, leva um redirecionamento
  // logo no primeiro segundo de uso.
  history.replaceState(null, '', '/');
  await getSupabase().auth.signOut();
  location.reload();
}
